import { useCallback } from 'react';

import { getOutstandingFormationTask, getRedirectServiceCode } from '#client/utils/formationPostPurchase';

import { useOrderTrackerStatus } from './useOrderTrackerStatus';
import { useUrlResolver } from './useUrlResolver';

// Services whose journey has nothing to collect until formation data is in.
const FORMATION_GATED_SERVICES = ['wfg_initial_report'];

/**
 * Resolves the post-purchase redirect for the full-page checkout flow. Thin wrapper
 * over the canonical resolver (`checkout_page` context):
 *   - `getCheckoutCompleteUrl` returns the destination URL, or `null` when the service
 *     has no configured redirect — preserving the original null-on-miss contract.
 *   - `handleCheckoutComplete` navigates there, or reloads the page to refresh
 *     entitlements when there is no URL.
 *
 * The service-code → route config lives in `serviceRoutes.js#checkout_page`. Its consumer
 * (CTAButton) depends on this exact surface.
 *
 * Buying a formation-gated service while formation still needs data redirects to
 * formation instead — its journey can't collect anything until that lands. Reading the
 * order tasks for that means this only runs inside `OrderTrackerStatusProvider`.
 *
 * @returns {{ getCheckoutCompleteUrl: Function, handleCheckoutComplete: Function }}
 */
export default function usePageCheckoutComplete() {
  const resolve = useUrlResolver();
  const { orderTasksWithStatus } = useOrderTrackerStatus();

  const getCheckoutCompleteUrl = useCallback(
    (serviceCode, orderUuid = null) => {
      const outstandingFormation = FORMATION_GATED_SERVICES.includes(serviceCode)
        ? getOutstandingFormationTask(orderTasksWithStatus)
        : null;
      const destination = outstandingFormation ? getRedirectServiceCode(outstandingFormation) : serviceCode;
      const { url, found } = resolve(destination, { context: 'checkout_page', orderUuid });

      return found ? url : null;
    },
    [resolve, orderTasksWithStatus],
  );

  const handleCheckoutComplete = useCallback(
    (serviceCode, orderUuid = null) => {
      const url = getCheckoutCompleteUrl(serviceCode, orderUuid);

      if (!url) {
        // Default behavior: reload the page to refresh entitlements
        window.location.reload();
        return;
      }

      window.location.href = url;
    },
    [getCheckoutCompleteUrl],
  );

  return {
    handleCheckoutComplete,
    getCheckoutCompleteUrl,
  };
}
